import type { Student } from "../types/database";
import { supabase } from "./supabase";
import { getImpersonatedUserId } from "./impersonate";

export async function getMyStudent(): Promise<Student | null> {
  // When an admin is viewing-as a student, read that student's row instead of
  // the admin's own. The admin's is_admin() RLS authorizes the read; nothing
  // is loosened. Writes never take this path (upsertMyStudent uses auth.uid()).
  let uid = getImpersonatedUserId();
  if (!uid) {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) return null;
    uid = userData.user.id;
  }

  const { data, error } = await supabase
    .from("students")
    .select("*")
    .eq("id", uid)
    .maybeSingle();

  if (error) {
    console.warn("[students] getMyStudent failed:", error.message);
    return null;
  }
  if (!data) return null;
  return data as Student;
}

type StudentPatch = Partial<Omit<Student, "id" | "updated_at">>;

export async function upsertMyStudent(patch: StudentPatch): Promise<{ error: Error | null }> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    return { error: new Error(userError?.message ?? "Not signed in") };
  }

  const uid = userData.user.id;

  const { data: existing, error: fetchError } = await supabase
    .from("students")
    .select("id")
    .eq("id", uid)
    .maybeSingle();

  if (fetchError) return { error: new Error(fetchError.message) };

  if (existing?.id) {
    const { error } = await supabase.from("students").update(patch).eq("id", uid);
    if (error) return { error: new Error(error.message) };
    return { error: null };
  }

  const { error } = await supabase.from("students").insert({ id: uid, ...patch });
  if (error) return { error: new Error(error.message) };
  return { error: null };
}
